import { useMemo } from "react";
import { Link } from "wouter";
import { EmptyState } from "../../components/AsyncStates";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PlacementStatusPill, StatePill } from "../../components/StatePill";
import { QueryStates } from "../../components/QueryStates";
import { FleetSubNav } from "../fleet/FleetSubNav";
import { type RouteRow, useRouteRows } from "./useRouteRows";
import "./routes.css";

/**
 * Every bound domain provisions TLS automatically: binding one is
 * conjunctively gated on `service:update` precisely because it causes a
 * public ACME certificate request for the hostname (see
 * `CreateDomainHandler`'s doc comment, `server/internal/projectsvc/
 * domain_http.go`). There is no opt-out and no per-domain "plaintext" mode
 * in the data model (ADR-0026 §2a, amended by ADR-0027 §2a's embedded
 * CertMagic) — so this badge renders a fixed architectural fact the same
 * way for every row, not a fabricated per-row live cert/issuance status
 * (charter S-15). Once the routing manifest (ADR-0027 §2b/§7) exists, a real
 * per-hostname status can replace it.
 */
function TlsBadge() {
  return <StatePill label="HTTPS (auto)" tone="positive" shape="dot" />;
}

function buildColumns(nodeName: (nodeId: string) => string): DataTableColumn<RouteRow>[] {
  return [
    {
      key: "hostname",
      header: "Hostname",
      cell: (row) => row.domain.hostname,
      className: "data-table__primary",
    },
    {
      key: "target",
      header: "Target",
      cell: (row) => (
        <span>
          <Link className="data-table__primary-link" href={`/projects/${row.project.id}/services/${row.service.id}`}>
            {row.project.name} / {row.service.name}
          </Link>
          <span className="data-table__secondary"> :{row.domain.targetPort}</span>
        </span>
      ),
    },
    {
      key: "tls",
      header: "TLS",
      cell: () => <TlsBadge />,
    },
    {
      key: "nodes",
      header: "Machines",
      cell: (row) =>
        row.placements.length === 0 ? (
          <span className="data-table__empty-cell">Not running anywhere</span>
        ) : (
          <ul className="routes-list__nodes">
            {row.placements.map((placement) => (
              <li key={placement.id} className="routes-list__node-row">
                <Link href={`/hosts/${placement.nodeId}`} className="data-table__primary-link">
                  {nodeName(placement.nodeId)}
                </Link>
                <PlacementStatusPill status={placement.status} />
              </li>
            ))}
          </ul>
        ),
    },
  ];
}

/**
 * Fleet-wide edge routing overview (ADR-0026 §5, scheduled by ADR-0027 §7
 * step 5): every bound hostname across every project, its target
 * service/port, and which nodes' placements currently serve it. Read-only —
 * binding/unbinding a domain, or placing a service, stays on the service
 * detail page (one write surface per resource, matching the rest of this
 * app). See `useRouteRows` for what is and isn't shown and why.
 */
export function RoutesListPage() {
  const { rows, isPending, isError, error, placementsDegraded, nodeName, refetch } = useRouteRows();
  const columns = useMemo(() => buildColumns(nodeName), [nodeName]);

  return (
    <>
      <FleetSubNav activeTab="routes" />
      <PageHeader
        title="Routes"
        description="Every domain bound to a service in this fleet, where it points, and which machines serve it."
      />

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading routes…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load routes. Check your connection and try again."
        }
        isEmpty={rows.length === 0}
        empty={
          <EmptyState
            title="No routes yet"
            description="Bind a domain to a service to route traffic to it."
          />
        }
      >
        {placementsDegraded && (
          <p className="routes-list__warning" role="status">
            Couldn't resolve which machines serve every route. Showing what loaded — try again in a moment.
          </p>
        )}
        <DataTable columns={columns} rows={rows} rowKey={(row) => row.domain.id} />
        <p className="routes-list__hint">
          Every bound hostname gets HTTPS automatically. This page doesn't yet show whether a certificate has
          actually been issued for a given hostname — only that Comebye will request one.
        </p>
      </QueryStates>
    </>
  );
}
